import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import * as api from '~/services/api';
import { Responsive } from '~/components';
import EmptyPage from '~/pages/EmptyPage';
import { Mobile, Desktop } from './responsive';
import * as utils from './utils';
import * as S from './styles';

export default function GlobalStatistics() {
  const { data: match } = useSelector(state => state.matches.selectedMatch);
  const {
    localSquad: {
      data: localSquad,
      loading: loadingVisitorSquad,
      failed: localFailed,
    },
    visitorSquad: {
      data: visitorSquad,
      loading: loadingLocalSquad,
      failed: visitorFailed,
    },
    selectedMatch: { data: selectedMatch, loading: loadingMatch },
  } = useSelector(state => state.matches);

  const [modalContentLeft, setModalContentLeft] = useState(null);
  const [modalContentRight, setModalContentRight] = useState(null);
  const [modalContentLeftInitial, setModalContentLeftInitial] = useState(null);
  const [modalContentRightInitial, setModalContentRightInitial] = useState(
    null
  );
  const [localTableData, setLocalTableData] = useState([]);
  const [visitorTableData, setVisitorTableData] = useState([]);
  const [visitorTableDataMobile, setVisitorTableDataMobile] = useState([]);

  useEffect(() => {
    if (Object.keys(localSquad).length && Object.keys(visitorSquad).length) {
      setLocalTableData(
        utils.tableDataParser({ data: localSquad.players, invert: false })
      );
      setVisitorTableData(
        utils.tableDataParser({ data: visitorSquad.players, invert: true })
      );
      setVisitorTableDataMobile(
        utils.tableDataParser({ data: visitorSquad.players, invert: false })
      );
    }
  }, [
    localSquad,
    visitorSquad,
    modalContentLeft,
    modalContentRight,
    modalContentLeftInitial,
    modalContentRightInitial,
  ]);

  const handleClickOnRowLeft = async row => {
    setModalContentRightInitial({ ...row, loading: true, isLocal: row.local });
    const {
      homeTeam: { id },
    } = selectedMatch;
    const { data } = await api.getSquadPlayer(
      id,
      row.original.id,
      selectedMatch.id
    );
    setModalContentRight({ ...row, isLocal: row.local, ...data });
  };

  const handleClickOnRowRight = async row => {
    setModalContentLeftInitial({ ...row, loading: true, isLocal: row.local });
    const {
      awayTeam: { id },
    } = selectedMatch;
    const { data } = await api.getSquadPlayer(
      id,
      row.original.id,
      selectedMatch.id
    );
    setModalContentLeft({ ...row, isLocal: row.local, ...data });
  };

  const handleCloseLeft = () => {
    setModalContentLeftInitial(null);
    setModalContentLeft(null);
  };

  const handleCloseRight = () => {
    setModalContentRightInitial(null);
    setModalContentRight(null);
  };

  const GlobalStatsTr = ({ item, ...itemProps }) => {
    return (
      <S.TrContainer
        {...itemProps}
        isTop5={item?.original?.isTop5}
        competition={match?.competitionId}
      />
    );
  };

  GlobalStatsTr.propTypes = {
    item: PropTypes.object,
  };

  GlobalStatsTr.defaultProps = {
    item: {},
  };

  const { columns, visitorColumns } = utils;
  const isFetching = loadingVisitorSquad || loadingLocalSquad || loadingMatch;
  const globalStatisticsProps = {
    columns,
    visitorColumns,
    handleClickOnRowRight,
    handleClickOnRowLeft,
    modalContentLeft,
    modalContentRight,
    localTableData,
    localHeadCoach: localSquad ? localSquad.headCoach : null,
    localSecondCoach: localSquad ? localSquad.secondCoach : null,
    visitorHeadCoach: visitorSquad ? visitorSquad.headCoach : null,
    visitorSecondCoach: visitorSquad ? visitorSquad.secondCoach : null,
    handleCloseLeft,
    handleCloseRight,
    selectedMatch,
    modalContentLeftInitial,
    modalContentRightInitial,
    isFetching,
    placeholderNumElements: 11,
    customTr: GlobalStatsTr,
  };

  const renderComponent = () => {
    if (
      !isFetching &&
      localTableData.length < 1 &&
      visitorTableData.length < 1 &&
      (visitorFailed || localFailed)
    )
      return <EmptyPage />;
    return (
      <Responsive
        sm={
          <Mobile
            visitorTableData={visitorTableDataMobile}
            {...globalStatisticsProps}
          />
        }
        lg={
          <Mobile
            tabletMode
            visitorTableData={visitorTableDataMobile}
            {...globalStatisticsProps}
          />
        }
        xl={
          <Desktop
            visitorTableData={visitorTableData}
            {...globalStatisticsProps}
          />
        }
      />
    );
  };

  return useMemo(renderComponent, [
    localTableData,
    visitorTableData,
    visitorTableDataMobile,
    isFetching,
  ]);
}
